import type { Subscription } from "../types/subscription"

/**
 * Statuts Stripe d'un abonnement qui n'a jamais été réellement actif et ne doit
 * donc pas s'afficher comme abonnement/module en cours :
 * - `incomplete` : paiement initial encore en attente (fenêtre de 23h).
 * - `incomplete_expired` : paiement initial jamais abouti, abonnement expiré par
 *   Stripe (typiquement un 1er checkout raté suivi d'une re-souscription).
 *
 * Ces lignes sont bien présentes dans le snapshot Mongo car il est construit via
 * l'API Stripe `status:'all'`, mais elles ne correspondent à aucune facturation
 * réelle côté client. Les laisser passer provoquait l'affichage en doublon d'un
 * module (ex. « Module Optimisation du Turpe ») et le gonflage du prix total.
 *
 * `canceled` est volontairement absent de cette liste : il est géré à part car
 * un abonnement annulé reste affiché (dernier plan résilié) pour les clients
 * churned.
 */
const NEVER_ACTIVE_STATUSES = ["incomplete", "incomplete_expired"]

/**
 * Indique si un abonnement doit s'afficher comme étant « en cours » (ni annulé,
 * ni jamais réellement activé).
 */
function isLiveSubscription(sub: Subscription): boolean {
    return (
        sub.status !== "canceled" && !NEVER_ACTIVE_STATUSES.includes(sub.status)
    )
}

/**
 * Formate les abonnements en regroupant les abonnements Energy Management / Energy Analytics avec leurs modules.
 * S'il n'y a pas d'abonnement actif EM/EA, renvoie le dernier abonnement annulé EM/EA et ses modules associés.
 * @param subscriptionsUnformatted - Liste des abonnements non formatés
 * @returns Liste des abonnements formatés avec leurs modules associés
 */
export function formatSubscriptions(
    subscriptionsUnformatted: Subscription[]
): Subscription[] {
    if (!subscriptionsUnformatted?.length) return []

    // Récupère les abonnements EA et EM (actifs ou non)
    const subscriptionEmEa = subscriptionsUnformatted.filter((sub) => {
        return (
            sub?.name?.includes("Energy Management -") ||
            sub?.name?.includes("Energy Analytics -")
        )
    })

    // Récupère les abonnements EM/EA réellement en cours (ni annulés, ni expirés
    // pour paiement initial non abouti)
    const subscriptionActive = subscriptionEmEa.filter(isLiveSubscription)
    // Récupère les modules (actifs ou annulés selon le cas)
    const modulesAll = subscriptionsUnformatted.filter((sub) =>
        sub?.name?.includes("Module ")
    )
    const modulesActive = modulesAll.filter(isLiveSubscription)

    // On trie les abonnements actifs : actif en premier, puis par date de début (plus récent en premier)
    const sortActiveByStatusThenDateStart = (
        a: Subscription,
        b: Subscription
    ) => {
        if (a.status === "active" && b.status !== "active") return -1
        if (a.status !== "active" && b.status === "active") return 1
        if (a.status !== "active" && b.status !== "active") {
            return (
                new Date(b.date_start).getTime() -
                new Date(a.date_start).getTime()
            )
        }
        return 0
    }

    const sortedSubscription = [...subscriptionActive].sort(
        sortActiveByStatusThenDateStart
    )
    let mainSubscription = sortedSubscription[0]

    let sortedModules: Subscription[]
    if (mainSubscription) {
        sortedModules = [...modulesActive].sort(sortActiveByStatusThenDateStart)
    } else {
        // Pas d'abonnement actif : afficher le dernier abonnement annulé EM/EA et ses modules annulés
        const subscriptionCanceled = subscriptionEmEa
            .filter((sub) => sub.status === "canceled")
            .sort(
                (a, b) =>
                    new Date(b.date_end).getTime() -
                    new Date(a.date_end).getTime()
            )
        mainSubscription = subscriptionCanceled[0]
        if (!mainSubscription) return []

        const modulesCanceled = modulesAll
            .filter((sub) => sub.status === "canceled")
            .sort(
                (a, b) =>
                    new Date(b.date_end).getTime() -
                    new Date(a.date_end).getTime()
            )
        sortedModules = modulesCanceled
    }

    return [
        {
            ...mainSubscription,
            modules: sortedModules,
        },
    ]
}

/**
 * Récupère les abonnements legacy (anciens abonnements non Energy Management/Analytics/Module)
 * @param subscriptions - Liste de tous les abonnements
 * @returns Liste des abonnements legacy
 */
export function getLegacySubscriptions(
    subscriptions: Subscription[]
): Subscription[] {
    if (!subscriptions?.length) return []

    return subscriptions
        .filter((subscription) => {
            return (
                !subscription.name?.includes("Energy Management -") &&
                !subscription.name?.includes("Energy Analytics -") &&
                !subscription.name?.includes("Module") &&
                isLiveSubscription(subscription)
            )
        })
        .map((subscription) => {
            return {
                ...subscription,
                modules: [],
                legacy: true,
            }
        })
}
